import styled from 'styled-components'
import { BaseModel } from '../../../../types/model'
import {
  Text,
  PrimaryAction,
  TertiaryAction,
  gaps,
  margins,
  paddings,
} from '@ohgoodlord/design-system'
import { track } from '../../../../../common/utils'
import { ReactNode, useCallback, useEffect } from 'react'
import { ActivityCallback } from '../../../../types/common'
import { useUserData } from '../../../../hooks/use-user-data'
import { faLock } from '@fortawesome/free-solid-svg-icons'
import { OverlayComponentProps } from '../../types'

export const OPEN_BANKING_O_AUTH = 'open-banking-o-auth' as const

export interface OpenBankingOverlayModel extends BaseModel<typeof OPEN_BANKING_O_AUTH> {
  baseUrl: string
  endpoint: string
  provider: string
  providerLogo: string
}

const StyledContainer = styled.div<{ $isMobile: boolean }>`
  position: relative;
  height: auto;
  display: flex;
  flex-direction: column;
  gap: ${gaps.m};
  padding-bottom: ${paddings.l};
`

const StyledProviderLogo = styled.div<{ $isMobile: boolean }>`
  width: 100%;
  max-width: ${({ $isMobile }) => ($isMobile ? '175px' : '200px')};
  margin: 0 auto;

  img {
    width: 100%;
  }
`

const StyledDescription = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${gaps.s};
  justify-content: center;
  align-items: center;
  max-width: 490px;
  margin: 0 auto ${margins.s};
  padding: 0 ${paddings.s};
`

const StyledBullets = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${gaps.s};
  padding: ${paddings.s} ${paddings.m};
`

const StyledBullet = styled.div`
  display: flex;
  gap: ${gaps.xs};
  align-items: flex-start;

  span {
    line-height: 20px;
  }
`

const StyledActionContainer = styled.div`
  display: flex;
  justify-content: center;
  margin: 0 0;
`

export type Props = OverlayComponentProps<OpenBankingOverlayModel>

export const OpenBankingOverlay = ({
  model,
  onClose,
  onSendMessage,
  onRegisterActivityCallback,
  isMobile,
  renderOverlayHeader,
}: Props) => {
  const { providerLogo } = model
  const { logUserData } = useUserData()

  const bulletPoints: { id: string; content: ReactNode }[] = [
    {
      id: 'provider',
      content: (
        <>
          Tink is an FCA-regulated Open Banking provider owned by Visa.{' '}
          <a href="https://tink.com/about-us/" target="_blank" rel="noopener noreferrer">
            About Tink
          </a>
        </>
      ),
    },
    { id: 'privacy', content: 'Your credit score isn’t affected.' },
    {
      id: 'security',
      content: 'Your bank login details will never be shared with Goodlord or Tink.',
    },
  ]

  const handleClose = useCallback(() => onClose(), [onClose])

  const handleRegisterActivityCallback = useCallback(
    (callback: ActivityCallback) => onRegisterActivityCallback(callback),
    [onRegisterActivityCallback],
  )

  const handleRedirect = () => {
    track('Click - Connect to open banking', {})

    // Log user details before redirect
    logUserData('Open Banking Redirect')

    const newWindow = window.open(
      model.baseUrl + model.endpoint,
      '_blank',
      'noopener,noreferrer',
    )

    if (newWindow) {
      newWindow.opener = null
    }
  }

  const handleHavingTrouble = () => {
    track('Click - Open banking help', {})
    onSendMessage({ message: "I'm having trouble" })
    handleClose()
  }

  useEffect(() => {
    const subscription = handleRegisterActivityCallback(activity => {
      if (activity.text !== undefined && activity.text === "I'm having trouble") {
        handleClose()
      }
    })

    return () => subscription.unsubscribe()
  }, [handleRegisterActivityCallback, handleClose])

  return (
    <StyledContainer $isMobile={isMobile}>
      {renderOverlayHeader !== undefined && renderOverlayHeader()}
      <StyledDescription>
        <Text align="center">
          It's like sending bank statements but easier, safer, and more private.
        </Text>
      </StyledDescription>
      <StyledProviderLogo $isMobile={isMobile}>
        <img src={`/assets/img/${providerLogo}`} alt={model.provider} />
      </StyledProviderLogo>
      <StyledDescription>
        <Text align="center">
          Tink verifies your income securely by only connecting to bank accounts you
          specify.
        </Text>
      </StyledDescription>
      <StyledActionContainer>
        <PrimaryAction onClick={handleRedirect} icon={faLock as any}>
          Securely connect a bank account
        </PrimaryAction>
      </StyledActionContainer>
      <StyledDescription>
        <StyledBullets>
          {bulletPoints.map(bullet => (
            <StyledBullet key={bullet.id}>
              <img
                src="/assets/img/greentick.png"
                style={{ width: '20px', height: '20px' }}
                alt=""
                role="presentation"
              />
              <Text>{bullet.content}</Text>
            </StyledBullet>
          ))}
        </StyledBullets>
      </StyledDescription>
      <StyledDescription>
        <TertiaryAction onClick={handleHavingTrouble}>I'm having trouble</TertiaryAction>
      </StyledDescription>
    </StyledContainer>
  )
}

export const OpenBankingOAuthFooter = () => null
